import React from 'react';
import type { PlaybookInstance } from '../types/playbook.types';

interface StuckBannerProps {
  instance: PlaybookInstance;
  isAdmin: boolean;
  onViewDiagnostics: () => void;
  onForceNext: () => void;
}

const StuckBanner: React.FC<StuckBannerProps> = ({ instance, isAdmin, onViewDiagnostics, onForceNext }) => {
  const isStuck = instance.error_state === 'stuck';
  const isSlaBreached = instance.error_state === 'sla_breached';
  const isActionFailed = instance.error_state === 'action_failed';

  if (!isStuck && !isSlaBreached && !isActionFailed) return null;

  const bannerConfig = isStuck
    ? {
        border: 'border-rose-300 dark:border-rose-700',
        bg: 'bg-rose-50 dark:bg-rose-900/20',
        text: 'text-rose-800 dark:text-rose-200',
        message: instance.stuck_reason || 'This workflow is blocked — no transitions are currently available.',
      }
    : isSlaBreached
      ? {
          border: 'border-amber-300 dark:border-amber-700',
          bg: 'bg-amber-50 dark:bg-amber-900/20',
          text: 'text-amber-800 dark:text-amber-200',
          message: `SLA exceeded — ${instance.currentStatus?.sla_hours ?? '?'}h limit in '${instance.currentStatus?.name ?? 'current status'}'`,
        }
      : {
          border: 'border-rose-300 dark:border-rose-700',
          bg: 'bg-rose-50 dark:bg-rose-900/20',
          text: 'text-rose-800 dark:text-rose-200',
          message: 'An action failed during the last transition. Review diagnostics for details.',
        };

  return (
    <div className={`rounded-md border ${bannerConfig.border} ${bannerConfig.bg} px-4 py-3 ${bannerConfig.text}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm font-medium">{bannerConfig.message}</p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onViewDiagnostics}
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            View Diagnostics
          </button>
          {isAdmin && (
            <button
              type="button"
              onClick={onForceNext}
              className="rounded-md border border-rose-300 bg-white px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50 dark:border-rose-700 dark:bg-rose-950/20 dark:text-rose-100 dark:hover:bg-rose-900/30"
            >
              Force Next Step
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default StuckBanner;
